// src/config/roleConfig.js - Role-based access control configuration

/**
 * Role Configuration
 * Defines which users have access to which pages and their default landing pages
 */

export const USER_ROLES = {
  BQM: 'bqm',
  FINANCE_MANAGER: 'financeManager',
  PLANNER: 'planner'
};

// Email to role mapping
export const emailToRole = (email) => {
  if (!email) return USER_ROLES.PLANNER;

  const emailLower = email.toLowerCase();

  if (emailLower === 'bqm@j49.com') {
    return USER_ROLES.BQM;
  }

  if (emailLower === 'financemanager@j49.com') {
    return USER_ROLES.FINANCE_MANAGER;
  }

  return USER_ROLES.PLANNER; // Default role for all other users
};

// Role-based page access configuration
export const roleAccess = {
  [USER_ROLES.BQM]: {
    homePage: '/j49/calendar',
    allowedPages: [
      '/j49/calendar',
      '/events',
      '/event/*',
      '/create-event',
      '/vendors'
    ],
    restrictedPages: [
      '/j49/finance'
    ]
  },
  [USER_ROLES.FINANCE_MANAGER]: {
    homePage: '/j49/finance',
    allowedPages: [
      '/j49/finance',
      '/events',
      '/event/*'
    ],
    restrictedPages: [
      '/j49/calendar',
      '/create-event',
      '/vendors'
    ]
  },
  [USER_ROLES.PLANNER]: {
    homePage: '/events',
    allowedPages: [
      '/*' // Planners have access to everything except J49 pages
    ],
    restrictedPages: [
      '/j49/calendar',
      '/j49/finance'
    ]
  }
};

// Check if user has access to a specific path
export const hasAccess = (userEmail, path) => {
  const role = emailToRole(userEmail);
  const config = roleAccess[role];

  if (!config) return true; // Allow access if no config (shouldn't happen)

  // Check if path is explicitly restricted
  const isRestricted = config.restrictedPages.some(restrictedPath => {
    if (restrictedPath.endsWith('*')) {
      const basePath = restrictedPath.slice(0, -1);
      return path.startsWith(basePath);
    }
    return path === restrictedPath || path.startsWith(restrictedPath + '/');
  });

  if (isRestricted) return false;

  // Check if path is in allowed pages
  const isAllowed = config.allowedPages.some(allowedPath => {
    if (allowedPath === '/*') return true;
    if (allowedPath.endsWith('*')) {
      const basePath = allowedPath.slice(0, -1);
      return path.startsWith(basePath);
    }
    return path === allowedPath || path.startsWith(allowedPath + '/');
  });

  return isAllowed;
};

// Get redirect path for unauthorized access
export const getRedirectPath = (userEmail, attemptedPath) => {
  const role = emailToRole(userEmail);
  const config = roleAccess[role];

  if (!config) return '/events'; // Fallback

  // If trying to access restricted page, redirect to home
  return config.homePage;
};

// Get user's home page based on email
export const getUserHomePage = (userEmail) => {
  const role = emailToRole(userEmail);
  const config = roleAccess[role];

  return config?.homePage || '/events';
};
